import React, { useState, useEffect, useMemo } from "react";
import { useSpring, animated } from "react-spring";
import { useInView } from "react-intersection-observer";
import LightboxImage from "./LightboxImage";

const ImageGallery = ({ images, showPagination = false }) => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [imagesPerPage, setImagesPerPage] = useState(6);
  const [searchTerm, setSearchTerm] = useState("");

  const normalizedSearchTerm = searchTerm.trim().toLowerCase();
  const filteredImages = useMemo(() => {
    if (!showPagination || !normalizedSearchTerm) return images;

    const keywords = normalizedSearchTerm.split(/\s+/);
    return images.filter((image) => {
      const description = (image.description || "").toLowerCase();
      return keywords.every((keyword) => description.includes(keyword));
    });
  }, [images, normalizedSearchTerm, showPagination]);

  const totalPages = Math.max(1, Math.ceil(filteredImages.length / imagesPerPage));
  const startIndex = showPagination ? (currentPage - 1) * imagesPerPage : 0;
  const endIndex = showPagination ? startIndex + imagesPerPage : filteredImages.length;
  const paginatedImages = filteredImages.slice(startIndex, endIndex);

  const handleImageClick = (image) => {
    setSelectedImage(image);
  };

  const handlePopoverClose = () => {
    setSelectedImage(null);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      setSelectedImage(null);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      setSelectedImage(null);
    }
  };

  const handleImagesPerPageChange = (e) => {
    setImagesPerPage(Number(e.target.value));
    setCurrentPage(1);
    setSelectedImage(null);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
    setSelectedImage(null);
  };

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
      setSelectedImage(null);
    }
  }, [currentPage, totalPages]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!selectedImage) return;

    const handleKeyDown = (e) => {
      if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
        e.preventDefault();
        
        const currentIndex = paginatedImages.findIndex(img => img.url === selectedImage.url);
        let newIndex;

        if (e.key === "ArrowLeft") {
          newIndex = currentIndex === 0 ? paginatedImages.length - 1 : currentIndex - 1;
        } else {
          newIndex = currentIndex === paginatedImages.length - 1 ? 0 : currentIndex + 1;
        }

        setSelectedImage(paginatedImages[newIndex]);
      } else if (e.key === "Escape") {
        handlePopoverClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedImage, paginatedImages]);

  const popoverAnimation = useSpring({
    opacity: selectedImage ? 1 : 0,
    transform: selectedImage ? "scale(1)" : "scale(0.98)",
    config: { tension: 300, friction: 20 },
  });

  return (
    <div className="container mx-auto p-2 sm:p-4">
      <div className="flex flex-wrap gap-4 justify-center">
        {paginatedImages.map((image, index) => (
          <LazyImageCard key={image.id || index} image={image} onClick={handleImageClick} />
        ))}
      </div>

      {showPagination && filteredImages.length === 0 && (
        <p className="mt-8 text-center text-[#70806f] font-semibold">
          No gallery images match that search.
        </p>
      )}

      {/* Pagination Controls */}
      {showPagination && (
        <div className="flex items-center justify-center gap-4 mt-8 flex-wrap">
          <button
            onClick={handlePreviousPage}
            disabled={currentPage === 1}
            className="px-4 py-2 bg-[#70806f] text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#5a6959] transition"
            aria-label="Previous page"
          >
            ← Previous
          </button>
          <span className="text-[#70806f] font-semibold">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
            className="px-4 py-2 bg-[#70806f] text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#5a6959] transition"
            aria-label="Next page"
          >
            Next →
          </button>
          
          <div className="flex items-center gap-2">
            <label htmlFor="images-per-page" className="text-[#70806f] font-semibold">
              Images per page:
            </label>
            <select
              id="images-per-page"
              value={imagesPerPage}
              onChange={handleImagesPerPageChange}
              className="px-3 py-2 bg-white text-[#70806f] border border-[#70806f] rounded-md hover:border-[#5a6959] transition cursor-pointer"
              aria-label="Select number of images per page"
            >
              <option value="6">6</option>
              <option value="9">9</option>
              <option value="12">12</option>
              <option value="15">15</option>
              <option value="18">18</option>
            </select>
          </div>
        </div>
      )}

      {showPagination && (
        <div className="mt-6 mx-auto max-w-xl">
          <label htmlFor="gallery-search" className="block text-[#70806f] font-semibold mb-2">
            Search gallery
          </label>
          <input
            id="gallery-search"
            type="search"
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder="flowers, tomatoes, compost"
            className="w-full px-4 py-3 bg-white text-[#314935] border border-[#70806f] rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#70806f] focus:border-[#70806f]"
            aria-label="Search gallery image descriptions"
          />
        </div>
      )}

      {selectedImage && (
        <div
          className="fixed inset-0 z-50 bg-black bg-opacity-80 flex items-center justify-center px-4"
          onClick={handlePopoverClose} // Close when clicking anywhere
        >
          <animated.div
            style={popoverAnimation}
            className="relative max-w-screen-xl w-full max-h-screen flex flex-col items-center"
          >
            {/* Close Button */}
            <button
              onClick={(e) => {
                e.stopPropagation(); // Don't trigger background close
                handlePopoverClose();
              }}
              className="absolute top-4 right-4 text-white text-3xl font-bold hover:text-red-400 transition z-10"
            >
              &times;
            </button>

            <LightboxImage
              src={selectedImage.url}
              placeholderSrc={selectedImage.thumbnailUrl}
              alt={selectedImage.description || "Selected Image"}
              aspectRatio={selectedImage.aspectRatio}
              maxHeight="80vh"
              frameClassName="rounded-md"
              imageClassName="max-h-[80vh] max-w-full object-contain"
            />

            {/* Optional Caption */}
            {selectedImage.description?.trim() && (
              <div className="mt-4 bg-black bg-opacity-50 text-white text-center px-6 py-3 rounded shadow-md max-w-3xl w-full">
                <p className="text-lg">{selectedImage.description}</p>
              </div>
            )}
          </animated.div>
        </div>
      )}
    </div>
  );
};

const LazyImageCard = ({ image, onClick }) => {
  const { ref, inView } = useInView({
    triggerOnce: true,
    rootMargin: "200px",
  });

  const [isLoaded, setIsLoaded] = useState(false);

  return (
    <div
      ref={ref}
      className="flex justify-center w-full max-w-xs sm:max-w-sm min-h-[250px] relative bg-gray-100"
    >
      {inView && (
        <img
          src={image.thumbnailUrl || image.url}
          alt={image.description || "Image"}
          loading="lazy"
          decoding="async"
          onLoad={() => setIsLoaded(true)}
          onClick={() => onClick(image)}
          className={`w-full h-auto aspect-square cursor-pointer object-cover rounded-md shadow-md transition-shadow hover:shadow-lg ${
            isLoaded ? "blur-0" : "blur-md"
          } transition-all duration-500`}
          style={{ opacity: isLoaded ? 1 : 0.7, transition: "opacity 0.5s ease" }}
        />
      )}
    </div>
  );
};

export default ImageGallery;
